import {
  Children,
  Fragment,
  MouseEvent,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useState,
} from "react";

import { Button, ButtonProps, Menu, MenuProps } from "@mui/material";

const NAME = "ButtonMenu";

export interface ButtonMenuProps extends ButtonProps {
  menu: ReactNode;
  menuProps?: Omit<MenuProps, "children" | "open">;
}

export const ButtonMenu = forwardRef<HTMLButtonElement, ButtonMenuProps>(
  ({ onClick, menu, menuProps, ...buttonProps }, buttonRef) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
      onClick?.(e);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <>
        <Button onClick={handleClick} ref={buttonRef} {...buttonProps} />
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClose={handleClose}
          open={Boolean(anchorEl)}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          {...menuProps}
        >
          {(() => {
            const processChildren = (children: ReactNode): ReactNode => {
              return Children.map(children, (child) => {
                if (!isValidElement(child)) return child;

                // Check if child is a Fragment
                if (
                  child.type === Fragment ||
                  child.type.toString() === "Symbol(react.fragment)"
                ) {
                  // Recursively process fragment children
                  return cloneElement(child as ReactElement, {
                    children: processChildren(child.props.children),
                  });
                }

                // For regular elements, add onClick handler
                return cloneElement(child as ReactElement, {
                  onClick: (e: MouseEvent<HTMLElement>) => {
                    handleClose();
                    if (child.props.onClick) child.props.onClick(e);
                  },
                });
              });
            };

            return processChildren(menu);
          })()}
        </Menu>
      </>
    );
  },
);
ButtonMenu.displayName = NAME;
